import React from 'react';
import { FiRefreshCw, FiCheckCircle, FiStar, FiTrendingUp } from 'react-icons/fi';

/**
 * QualityScoreBadge - Displays quality score with color coding
 * 
 * Quality Tiers:
 * - Premium (≥7.0): Green - High quality
 * - Good (6.0-6.9): Blue - Solid quality
 * - Needs Work (< 6.0): Yellow - Consider regenerating
 * 
 * Props:
 * - score: number (1-10)
 * - showLabel: boolean - Show tier label
 * - onRegenerate: function - Callback when regenerate clicked (shows button if provided)
 * - size: 'sm' | 'md' | 'lg'
 * - className: string - Additional classes
 */
export function QualityScoreBadge({ 
  score, 
  showLabel = false,
  onRegenerate = null,
  size = 'md',
  className = ''
}) {
  if (score === null || score === undefined) return null;

  // Determine tier and styling - simplified, no drama
  const getTierInfo = (score) => {
    if (score >= 7.0) {
      return {
        tier: 'premium',
        label: 'Great',
        bgClass: 'bg-green-500/20',
        textClass: 'text-green-400',
        icon: FiCheckCircle
      };
    } else if (score >= 6.0) {
      return {
        tier: 'good',
        label: 'Good',
        bgClass: 'bg-blue-500/20',
        textClass: 'text-blue-400',
        icon: FiStar
      };
    } else {
      return {
        tier: 'ok',
        label: 'OK',
        bgClass: 'bg-yellow-500/20',
        textClass: 'text-yellow-400',
        icon: FiTrendingUp
      };
    }
  };

  const tierInfo = getTierInfo(score);
  const Icon = tierInfo.icon;

  // Size classes
  const sizeClasses = {
    sm: 'px-1.5 py-0.5 text-xs',
    md: 'px-2 py-1 text-sm',
    lg: 'px-3 py-1.5 text-base'
  };

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      {/* Score Badge */}
      <span 
        className={`inline-flex items-center gap-1 rounded font-semibold ${tierInfo.bgClass} ${tierInfo.textClass} ${sizeClasses[size]}`}
      >
        <Icon size={size === 'sm' ? 12 : size === 'md' ? 14 : 16} />
        {showLabel && <span>{tierInfo.label}</span>}
        <span>{score.toFixed(1)}/10</span>
      </span>

      {/* Regenerate Button - only show if callback provided */}
      {onRegenerate && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRegenerate();
          }}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-primary/20 text-primary hover:bg-primary/30 transition-colors"
          title="Generate a new version"
        >
          <FiRefreshCw size={12} />
          <span>Retry</span>
        </button>
      )}
    </div>
  );
}

/**
 * QualityGateIndicator - Shows quality optimization status (subtle)
 * Only shows if optimization actually happened
 */
export function QualityGateIndicator({ qualityGate, className = '' }) {
  if (!qualityGate) return null;

  const { items_retried, time_spent_seconds } = qualityGate;

  // Don't show anything if no optimization happened
  if (!items_retried || items_retried === 0) {
    return null;
  }

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs bg-green-500/10 text-green-400 ${className}`}>
      <FiCheckCircle size={12} />
      <span>Quality optimized</span>
      {time_spent_seconds > 0 && (
        <span className="text-green-400/60">• {time_spent_seconds}s</span>
      )}
    </span>
  );
}

/**
 * QualityScoreBreakdown - Detailed score breakdown component
 */
export function QualityScoreBreakdown({ scoreBreakdown, verdict, className = '' }) {
  if (!scoreBreakdown) return null;

  const dimensions = [
    { key: 'hook_score', label: 'Hook', icon: '🎣' },
    { key: 'caption_score', label: 'Caption', icon: '✍️' },
    { key: 'visual_score', label: 'Visual', icon: '🎨' },
    { key: 'brand_alignment', label: 'Brand Fit', icon: '🎯' },
    { key: 'platform_fit', label: 'Platform', icon: '📱' },
    { key: 'relevance', label: 'Relevance', icon: '🎯' },
    { key: 'differentiation', label: 'Unique', icon: '✨' },
    { key: 'clarity', label: 'Clarity', icon: '💡' },
    { key: 'commercial', label: 'Commercial', icon: '💰' },
    { key: 'brand_fit', label: 'Brand Fit', icon: '🏷️' },
  ];

  const availableDimensions = dimensions.filter(d => scoreBreakdown[d.key] !== undefined);

  if (availableDimensions.length === 0) return null;

  const getScoreColor = (score) => {
    if (score >= 7) return 'text-green-400';
    if (score >= 6) return 'text-blue-400';
    if (score >= 5) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div className={`p-3 rounded-lg bg-card/50 border border-border ${className}`}>
      <div className="flex flex-wrap gap-3 mb-2">
        {availableDimensions.map(({ key, label, icon }) => (
          <div key={key} className="flex items-center gap-1">
            <span className="text-xs">{icon}</span>
            <span className="text-xs text-muted-foreground">{label}:</span>
            <span className={`text-xs font-semibold ${getScoreColor(scoreBreakdown[key])}`}>
              {scoreBreakdown[key]}
            </span>
          </div>
        ))}
      </div>
      {verdict && (
        <p className="text-xs text-primary italic">💡 {verdict}</p>
      )}
    </div>
  );
}

export default QualityScoreBadge;
